import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { T, LANGS } from "@/i18n/translations";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const LangContext = createContext(null);

const DEFAULT = (() => {
  const saved = localStorage.getItem("lang");
  if (saved && LANGS.some((l) => l.code === saved)) return saved;
  const nav = (navigator.language || "it").slice(0, 2);
  return LANGS.some((l) => l.code === nav) ? nav : "it";
})();

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT);
  const setLang = useCallback((code, persist = true) => { if (persist) localStorage.setItem("lang", code); setLangState(code); }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const t = useCallback((key, vars) => {
    const entry = T[key];
    if (!entry) return key;
    return interpolate(entry[lang] || entry.it || entry.en || key, vars);
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useT() {
  const ctx = useContext(LangContext);
  return ctx ? ctx.t : (k) => k;
}

export function useLang() {
  const ctx = useContext(LangContext);
  return ctx || { lang: "it", setLang: () => {} };
}

export function LanguageSwitcher({ className = "" }) {
  const { lang, setLang } = useLang();
  const current = LANGS.find((l) => l.code === lang);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-card text-sm hover:border-white/40 transition-colors ${className}`} data-testid="language-switcher">
          <span className="text-base leading-none">{current?.flag}</span>
          <span className="uppercase font-semibold">{lang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border max-h-64 overflow-y-auto w-44">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
            className={`cursor-pointer gap-2 ${l.code === lang ? "text-white font-semibold" : "text-muted-foreground"}`}>
            <span className="text-base leading-none">{l.flag}</span> {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
